import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import { userIsAny } from '../../auth/authUtils';
import userTypes from '../../../constants/userTypes';
import { useTranslation } from 'react-i18next';
import { gamesColumnDefinitions } from './GamesColumnDefinitions';
import { useAppSelector } from '../../../hooks';
import { exportGameListData, getGames } from '../../../api/gameApi';

export interface GameListProps {
  reload: any;
  itemsChecked: any[];
  setItemsChecked: (items: any[]) => void;
  onRequestKeys: (e: any, item: any) => void;
  onSteamBulkRequest: (e: any, items: any[]) => void;
  setBulkActionsSlideoutOpen: (open: boolean) => void;
  tableViewSwitcher: React.ReactNode;
}

function GameList({
  reload,
  itemsChecked,
  setItemsChecked,
  onRequestKeys,
  onSteamBulkRequest,
  setBulkActionsSlideoutOpen,
  tableViewSwitcher,
}: GameListProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const currentUser = useAppSelector((state) => state.auth);

  let allColumns = [
    'gameName',
    'franchise',
    'publisher',
    'developer',
    'appId',
    'skuCount',
    'openRequests',
    'isArchived',
    'creationDate',
  ];

  if (
    userIsAny(currentUser, [
      userTypes.ADMIN,
      userTypes.APPROVER,
      userTypes.UPLOADER,
    ])
  ) {
    allColumns = [...allColumns, 'requestActivity', 'keyActivity'];
  }

  const suffixColumns = ['buttons-checkbox'];

  const defaultVisibleColumns = [
    'gameName',
    'franchise',
    'appId',
    'skuCount',
    'openRequests',
    'isArchived',
    'creationDate',
  ];

  const [games, setGames] = useState<any>();
  const params = new URLSearchParams(location.search);

  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'gameList-v3-settings',
    getTableSettingsDefaults({
      defaultSort: 'gameName',
      defaultVisibleColumns,
    }),
    null,
    getGames,
    setGames,
    reload,
    t('Failed to load games')
  );

  const handleCheckAllChanged = (checked: boolean) => {
    checked
      ? setItemsChecked(games?.data?.filter((x: any) => !x.isArchived) ?? [])
      : setItemsChecked([]);
  };
  const handleCheckChanged = (checked: boolean, item: any) =>
    checked
      ? setItemsChecked([...itemsChecked, item])
      : setItemsChecked(itemsChecked.filter((x: any) => x.gameId !== item.gameId));
  const isChecked = (item: any) =>
    itemsChecked?.some((x: any) => x.gameId === item.gameId);

  const columnsConfig = gamesColumnDefinitions({
    currentUser,
    params,
    filterChange,
    games,
    onRequestKeys,
    onSteamBulkRequest,
    handleCheckAllChanged,
    handleCheckChanged,
    isChecked,
  });

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = suffixColumns
    .map((x) => columnsConfig.find((y: any) => y.Key === x))
    .filter((x) => x);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);
  // return an alaska table
  return games ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        className="gamesTable"
        rows={games.data.map((x: any) => {
          return { Key: x.gameId, ...x };
        })}
        totalRowCount={games.totalRowCount}
        filteredRowCount={games.filteredRowCount}
        scopes={games.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={(filters, scopes) =>
          exportGameListData({
            filters: (filters ?? {}) as Record<string, string>,
            scopes: (scopes ?? {}) as Record<string, string>,
          })
        }
        setBulkActionsSlideoutOpen={setBulkActionsSlideoutOpen}
        itemsChecked={itemsChecked}
        bulkActionTitle={
          itemsChecked.length === 0
            ? t('Bulk actions - first tick some games')
            : t('Bulk actions')
        }
        testId="games-list-table"
        tableActionBarButtons={tableViewSwitcher}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}


export default GameList;
